// src/adm/pages/User.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AdmNav from "../components/AdmNav";
import ToggleSwitch from "../components/ToggleSwitch";
import { AdmBranchAPI, AdmUserAPI } from "../api";

/** 지역 헤더가 있는 지점 셀렉트 (브랜치 페이지와 동일 패턴) */
function BranchSelect({ options, value, onChange }) {
  const grouped = useMemo(() => {
    const list = [];
    let currentArea = "";
    options.forEach((b) => {
      if (b.branch_area !== currentArea) {
        currentArea = b.branch_area;
        list.push({ type: "header", text: `-----${currentArea}-----` });
      }
      list.push({
        type: "item",
        value: String(b.branch_name), // 검색은 branch_name으로 수행
        text: b.branch_name,
      });
    });
    return list;
  }, [options]);

  return (
    <select
      id="branch"
      className="border rounded-md px-3 py-1 focus:outline-none"
      value={value}
      onChange={(e) => onChange(e.target.value)} // 선택 즉시 검색 트리거
    >
      <option value="">지점선택</option>
      {grouped.map((opt, i) =>
        opt.type === "header" ? (
          <option key={`h-${i}`} value="" disabled>
            {opt.text}
          </option>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.text}
          </option>
        )
      )}
    </select>
  );
}

/** 회원 추가 모달 */
function AddUserModal({ open, onClose, onSubmit, branches }) {
  const [branchIdx, setBranchIdx] = useState("");
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(true);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      setBranchIdx("");
      setId("");
      setName("");
      setPhone("");
      setEmail("");
      setPassword(""); // reset
      setActive(true);
      setPending(false);
      setErr("");
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchIdx) return setErr("지점을 선택하세요.");
    if (!id.trim()) return setErr("ID를 입력하세요.");
    if (!name.trim()) return setErr("회원명을 입력하세요.");

    try {
      setPending(true);
      setErr("");

      // ✅ branch_name은 서버가 필요로 할 수도 있어 보조로 찾아 전달
      const b = branches.find((x) => String(x.branch_idx) === String(branchIdx));
      const branch_name = b?.branch_name || "";

      await onSubmit({
        branch_idx: Number(branchIdx),
        branch_name,
        id: id.trim(),
        user_name: name.trim(),
        phone_number: phone.trim(),
        email: email.trim(),
        password: password || "0000",
        status: active ? 1 : 9,
      });
    } catch (e2) {
      setErr(e2?.message || "등록 중 오류");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="adm-modal-backdrop" onClick={onClose}>
      <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="adm-modal-title">회원 추가</h3>

        <form onSubmit={handleSubmit} className="adm-modal-form">
          <label className="adm-field">
            <span className="adm-label">지점명</span>
            <select value={branchIdx} onChange={(e) => setBranchIdx(e.target.value)}>
              <option value="">지점을 선택하세요</option>
              {branches.map((b) => (
                <option key={b.branch_idx} value={b.branch_idx}>
                  {b.branch_name}
                </option>
              ))}
            </select>
          </label>

          <label className="adm-field">
            <span className="adm-label">ID</span>
            <input placeholder="로그인 ID" value={id} onChange={(e) => setId(e.target.value)} />
          </label>

          <label className="adm-field">
            <span className="adm-label">회원명</span>
            <input placeholder="회원명을 입력하세요" value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="adm-field">
            <span className="adm-label">전화번호</span>
            <input placeholder="010-0000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>

          <label className="adm-field">
            <span className="adm-label">이메일</span>
            <input placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>

          <label className="adm-field">
            <span className="adm-label">비밀번호</span>
            <input placeholder="초기 비밀번호 (미입력 시 0000)" value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>

          <label className="adm-field">
            <span className="adm-label">상태</span>
            <ToggleSwitch checked={active} onChange={setActive} />
          </label>

          {err && <p className="adm-error">{err}</p>}

          <div className="adm-modal-actions">
            <button type="button" className="btn cancel" onClick={onClose} disabled={pending}>
              취소
            </button>
            <button type="submit" className="btn primary" disabled={pending}>
              {pending ? "추가 중..." : "추가"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UserPage() {
  const location = useLocation();

  // 지점 목록(셀렉트), 회원 목록(테이블)
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);

  // 필터
  const [selectedBranch, setSelectedBranch] = useState("");
  const [keyword, setKeyword] = useState("");

  // UI 상태
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openAdd, setOpenAdd] = useState(false);

  // 최초 로드
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const list = await AdmBranchAPI.list(); // 전체 지점
        setBranches(Array.isArray(list) ? list : []);
        // URL 쿼리 반영 (예: /adm/user?branch_name=강남본점)
        const sp = new URLSearchParams(location.search);
        const qBranch = sp.get("branch_name") || "";
        setSelectedBranch(qBranch);
        const initRows = await AdmUserAPI.list({
          branch_name: qBranch || undefined,
          keyword: "",
        });
        setRows(initRows);
      } catch (e) {
        setErr(e?.message || "로드 실패");
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 검색
  const search = async ({ branch = selectedBranch, q = keyword } = {}) => {
  try {
    setLoading(true);
    setErr("");

    // 1) 쿼리스트링 구성
    const qs = new URLSearchParams();

    // 지점: 숫자면 branch_idx, 아니면 branch_name
    const branchStr = String(branch || "").trim();
    if (branchStr) {
      if (/^\d+$/.test(branchStr)) {
        qs.set("branch_idx", branchStr);
      } else {
        qs.set("branch_name", branchStr);
      }
    }

    // 회원명만 검색
    const qTrim = (q || "").trim();
    if (qTrim) qs.set("user_name", qTrim);

    const base = (typeof window !== "undefined" && window.API_BASE_URL) || "";
    const url = `${base}/api/getUser${qs.toString() ? `?${qs}` : ""}`;

    // 2) 서버 호출
    const res = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${localStorage.getItem("adm_token") || ""}` },
      credentials: "include",
    });
    const data = await res.json();
    let rows = (Array.isArray(data) && data) || (Array.isArray(data?.data) && data.data) || [];

    // 3) 안전 보정(서버가 필터를 무시하는 경우 대비)
    const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();

    // 회원명 보정
    if (qTrim) {
      const qNorm = norm(qTrim);
      rows = rows.filter((r) => norm(r.user_name).includes(qNorm));
    }

    // 지점 보정: 숫자면 idx로, 아니면 이름으로 매칭
    if (branchStr) {
      if (/^\d+$/.test(branchStr)) {
        rows = rows.filter((r) => String(r.branch_idx || "") === branchStr);
      } else {
        rows = rows.filter((r) => norm(r.branch_name) === norm(branchStr));
      }
    }

    setRows(rows);
  } catch (e) {
    setErr(e?.message || "검색 실패");
    setRows([]);
  } finally {
    setLoading(false);
  }
};

  // 셀렉트 선택 즉시 검색
  const onSelectBranch = (val) => {
    setSelectedBranch(val);
    search({ branch: val, q: keyword });
  };

  // 상태 토글
  const onToggle = async (row, next) => {
    const prev = rows.slice();
    try {
      setRows((old) => old.map((it) => (it.id === row.id ? { ...it, status: next ? 1 : 9 } : it)));
      await AdmUserAPI.toggleStatus({
        id: row.id, // ✅ 레거시: 키 이름은 id
        status: next ? 1 : 9, // ✅ 숫자
      });
    } catch (e) {
      setRows(prev);
      alert(e?.message || "상태 변경 실패");
    }
  };

  // 회원 추가
  const onCreate = async (payload) => {
    await AdmUserAPI.create(payload);
    setOpenAdd(false);
    await search();
  };

  // 회원 삭제
  const onDelete = async (row) => {
    if (!window.confirm(`'${row.user_name || row.id}' 회원을 삭제하시겠습니까?`)) return;
    try {
      await AdmUserAPI.deleteUser(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e?.message || "삭제 실패");
    }
  };

  return (
    <>
      <AdmNav />
      <section className="adm-branch">
        <div className="adm-card">
          <div className="adm-card-head">
            {/* 좌측: 제목 + 회원 추가 */}
            <div className="adm-title-row">
              <h2>회원 리스트</h2>
              <button className="btn primary" onClick={() => setOpenAdd(true)}>
                + 회원 추가
              </button>
            </div>

            {/* 우측: 지점 선택 + 키워드 검색 */}
            <div className="adm-toolbar">
              <BranchSelect options={branches} value={selectedBranch} onChange={onSelectBranch} />
              <input
                className="border rounded-md px-3 py-1 ml-2"
                placeholder="회원명 입력"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") search();
                }}
              />
              <button className="btn ml-2" onClick={() => search()}>
                검색
              </button>
            </div>
          </div>

          <div className="adm-table-wrap">
            <table className="adm-table" cellPadding={0} cellSpacing={0}>
              <thead>
                <tr className="table-header">
                  <th>지점명</th>
                  <th>id</th>
                  <th>회원명</th>
                  <th>전화번호</th>
                  <th>이메일</th>
                  <th>사용량</th>
                  <th>상태</th>
                  <th>가입일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="center">
                      로딩 중…
                    </td>
                  </tr>
                ) : err ? (
                  <tr>
                    <td colSpan={9} className="center error">
                      {err}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="center">
                      결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  rows.map((u) => {
                    const joinDate = u.reg_date?.slice?.(0, 10) || (typeof u.created_at === "string" ? u.created_at.slice(0, 10) : "") || (typeof u.createdAt === "string" ? u.createdAt.slice(0, 10) : "");
                    return (
                      <tr key={u.id} className="table-row border-b">
                        <td className="p-3">{u.branch_name}</td>
                        <td className="p-3">{u.id}</td>
                        <td className="p-3">{u.user_name}</td>
                        <td className="p-3">{u.phone_number || ""}</td>
                        <td className="p-3">{u.email || ""}</td>
                        <td className="p-3">{u.total_api_calls ?? 0}</td>
                        <td className="p-3">
                          <ToggleSwitch checked={String(u.status) === "1" || u.status === 1} onChange={(next) => onToggle(u, next)} />
                        </td>
                        <td className="p-3">{joinDate}</td>
                        <td className="p-3">
                          <button
                            className="btn danger"
                            onClick={() => onDelete(u)}
                          >
                            삭제
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 회원 추가 모달 */}
        <AddUserModal open={openAdd} onClose={() => setOpenAdd(false)} onSubmit={onCreate} branches={branches} />
      </section>
    </>
  );
}
